// @flow
import type {
  InputCrop,
  PersistedImage,
  PersistedVideo,
  Crop,
  UrlCheckResult,
  ValidatedAdvertisementWithStoredMedia,
  InputCrops,
  ProviderTemplateFieldsInfo,
  ControlsInfo,
  InputCarousel
} from '../../components/types'
import type { FieldsState, CommonFields, MediaState } from './advertisementTemplate/reducers'
import { putMedia } from './media/operations'
import { putAdvertisement } from './advertisements/operations'
import { putCarousel } from './carouselEdit/operations'
import { mapProviderTemplatesDataToControlsData } from '../../components/connectProviderTemplatesWithControls'
import _ from 'lodash'

type AdvertisementTemplateRelations = $Shape<ProviderTemplateFieldsInfo<any>>

export type RawAdvertisement = {|
  id?: ?number,
  advertisement_template_id: ?number,
  url?: string,
  title?: string,
  description?: string,
  image?: ?PersistedImage,
  smallImage?: ?PersistedImage,
  smallImageCrops?: ?InputCrops,
  video?: ?PersistedVideo,
  templates: AdvertisementTemplateRelations,
  url_check: ?UrlCheckResult
|}

export type RawAdvertisementTemplate = {|
  name: string,
  advertisements: Array<{| audience_id: number |}>,
  provider_templates: AdvertisementTemplateRelations,
  url_check?: UrlCheckResult
|}

export function singleCrop(data: ?InputCrop): Crop | null {
  if (!data) return null

  return {
    x: data.left,
    y: data.top,
    width: data.width,
    height: data.height
  }
}

export function putRawAdvertisement(advertisement: RawAdvertisement): string {
  const templates: AdvertisementTemplateRelations = advertisement.templates
  const fs = adaptCommonTemplateFields(null, 'dummy name', templates)
  const ms = adaptMediaTemplateFields(templates)

  const storedAdvertisement: ValidatedAdvertisementWithStoredMedia = {
    advertisementTemplateId: advertisement && advertisement.advertisement_template_id,
    carouselStoreId: fs.carouselStoreId,
    columnDescription: fs.columnDescription,
    columnDescriptionErrors: [],
    columnImageErrors: [],
    columnImageStoreId: ms.columnImageStoreId,
    fbImageCrop: ms.fbImageCrop,
    fbInstagramImageCrop: ms.fbInstagramImageCrop,
    fbInstagramImageErrors: [],
    fbInstagramImageStoreId: ms.fbInstagramImageStoreId,
    fbLinkButton: fs.fbLinkButton,
    fbLinkButtonErrors: [],
    fbMultiDescription: fs.fbMultiDescription,
    fbMultiDescriptionErrors: [],
    fbStoriesImageCrop: ms.fbStoriesImageCrop,
    fbStoriesImageErrors: [],
    fbStoriesImageStoreId: ms.fbStoriesImageStoreId,
    fbThumbnailImageCrop: ms.fbThumbnailImageCrop,
    fbThumbnailImageErrors: [],
    fbThumbnailImageStoreId: ms.fbThumbnailImageStoreId,
    fbTitle: fs.fbTitle,
    fbTitleErrors: [],
    fbUrl: fs.fbUrl,
    fbUrlErrors: [],
    glImageCrop: ms.glImageCrop,
    id: advertisement && advertisement.id,
    logoImageCrop: fs.logoImageCrop,
    logoImageErrors: [],
    logoImageStoreId: fs.logoImageStoreId,
    multiDescription: fs.multiDescription,
    multiDescriptionErrors: [],
    multiImageErrors: [],
    multiImageStoreId: ms.multiImageStoreId,
    productDescription: fs.productDescription,
    productDescriptionErrors: [],
    productTitle: fs.productTitle,
    productTitleErrors: [],
    snippetImageCrop: fs.snippetImageCrop,
    snippetImageErrors: [],
    snippetImageStoreId: fs.snippetImageStoreId,
    title: fs.title,
    titleErrors: [],
    tmCarouselDescription: fs.tmCarouselDescription,
    tmCarouselDescriptionErrors: [],
    tmColumnCrop: ms.tmColumnImageCrop,
    tmLinkButton: fs.tmLinkButton,
    tmLinkButtonErrors: [],
    tmMultiCrop: ms.tmMultiImageCrop,
    tmSmallImageCrop: ms.tmSmallImageCrop,
    tmSmallImageErrors: [],
    tmSmallImageStoreId: ms.tmSmallImageStoreId || ms.multiImageStoreId,
    tmUrl: fs.tmUrl,
    tmUrlErrors: [],
    url: fs.url,
    urlErrors: [],
    videoErrors: [],
    videoStoreId: ms.videoStoreId,
    vkArticleCrop: ms.vkArticleImageCrop,
    vkBigImageErrors: [],
    vkBigImageStoreId: ms.vkBigImageStoreId,
    vkColumnCrop: ms.vkColumnImageCrop,
    vkLinkButton: fs.vkLinkButton,
    vkLinkButtonErrors: [],
    vkLinkTitle: fs.vkLinkTitle,
    vkLinkTitleErrors: [],
    vkMultiCrop: ms.vkMultiImageCrop,
    vkMultiDescription: fs.vkMultiDescription,
    vkMultiDescriptionErrors: [],
    vkSiteCrop: ms.vkSiteImageCrop,
    vkTitle: fs.vkTitle,
    vkTitleErrors: [],
    vkUrl: fs.vkUrl,
    vkUrlErrors: [],
    vkWithoutButtonDescription: fs.vkWithoutButtonDescription,
    vkWithoutButtonDescriptionErrors: []
  }

  return putAdvertisement(storedAdvertisement)
}

function adaptCarousel(controlsData: ControlsInfo<any>): InputCarousel {
  const carousels = _.filter([
    controlsData.vkCarousel,
    controlsData.tmCarousel,
    controlsData.fbCarousel
  ])
  const resultCards: Object = []
  _.each(carousels, (carousel) => {
    _.each(carousel.cards, (card, index: number) => {
      while (resultCards.length <= index) resultCards.push({})
      resultCards[index] = { ...resultCards[index], ...card }
    })
  })
  return { cards: resultCards }
}

function adaptCommonTemplateFields(
  id: ?number,
  name: string,
  providerTemplates: AdvertisementTemplateRelations
): CommonFields {
  const controlsData = mapProviderTemplatesDataToControlsData(
    providerTemplates,
    null,
    (a, b) => a || b
  )

  const diff: CommonFields = {
    id: id,
    name: name,
    url: controlsData.url || '',
    vkUrl: controlsData.vkUrl || '',
    tmUrl: controlsData.tmUrl || '',
    fbUrl: controlsData.fbUrl || '',
    title: controlsData.title || '',
    productTitle: controlsData.productTitle || '',
    vkTitle: controlsData.vkTitle || '',
    fbTitle: controlsData.fbTitle || '',
    columnDescription: controlsData.columnDescription || '',
    multiDescription: controlsData.multiDescription || '',
    productDescription: controlsData.productDescription || '',
    vkMultiDescription: controlsData.vkMultiDescription || '',
    vkWithoutButtonDescription: controlsData.vkWithoutButtonDescription || '',
    vkLinkTitle: controlsData.vkLinkTitle,
    fbMultiDescription: controlsData.fbMultiDescription || '',
    tmCarouselDescription: controlsData.tmCarouselDescription || '',
    vkLinkButton: controlsData.vkLinkButton,
    tmLinkButton: controlsData.tmLinkButton,
    fbLinkButton: controlsData.fbLinkButton,
    logoImageStoreId: controlsData.logoImageData && putMedia(controlsData.logoImageData),
    logoImageCrop: singleCrop(controlsData.logoCrop),
    snippetImageStoreId: controlsData.snippetImageData && putMedia(controlsData.snippetImageData),
    snippetImageCrop: singleCrop(controlsData.snippetCrop),
    carouselStoreId: putCarousel(adaptCarousel(controlsData))
  }

  return diff
}

function adaptMediaTemplateFields(providerTemplates: AdvertisementTemplateRelations): MediaState {
  const controlsData = mapProviderTemplatesDataToControlsData(
    providerTemplates,
    null,
    (a, b) => a || b
  )

  const diff: MediaState = {
    columnImageStoreId: controlsData.columnImageData && putMedia(controlsData.columnImageData),
    multiImageStoreId: controlsData.multiImageData && putMedia(controlsData.multiImageData),
    fbInstagramImageStoreId:
      controlsData.fbInstagramImageData && putMedia(controlsData.fbInstagramImageData),
    fbStoriesImageStoreId:
      controlsData.fbStoriesImageData && putMedia(controlsData.fbStoriesImageData),
    tmSmallImageStoreId: controlsData.tmSmallImageData && putMedia(controlsData.tmSmallImageData),
    vkColumnImageCrop: singleCrop(controlsData.vkColumnCrop),
    vkMultiImageCrop: singleCrop(controlsData.vkMultiCrop),
    vkArticleImageCrop: singleCrop(controlsData.vkArticleCrop),
    vkSiteImageCrop: singleCrop(controlsData.vkSiteCrop),
    tmColumnImageCrop: singleCrop(controlsData.tmColumnCrop),
    tmMultiImageCrop: singleCrop(controlsData.tmMultiCrop),
    tmSmallImageCrop: singleCrop(controlsData.tmSmallCrop),
    fbImageCrop: singleCrop(controlsData.fbMultiCrop),
    fbInstagramImageCrop: singleCrop(controlsData.fbInstagramCrop),
    fbStoriesImageCrop: singleCrop(controlsData.fbStoriesCrop),
    glImageCrop: singleCrop(controlsData.glMultiCrop),
    videoStoreId: controlsData.videoData && putMedia(controlsData.videoData),
    fbThumbnailImageStoreId:
      controlsData.fbThumbnailImageData && putMedia(controlsData.fbThumbnailImageData),
    fbThumbnailImageCrop: singleCrop(controlsData.fbThumbnailCrop),
    vkBigImageStoreId: controlsData.vkBigImageData && putMedia(controlsData.vkBigImageData)
  }

  return diff
}

export function adaptRawAdvertisementTemplate(
  id: ?number,
  template: RawAdvertisementTemplate
): FieldsState {
  return {
    common: adaptCommonTemplateFields(id, template.name, template.provider_templates),
    media: [adaptMediaTemplateFields(template.provider_templates)]
  }
}
